import { ArrowRightAlt } from "@ellaandil/ui";
import { clsx } from "@trydig/ui";
import type { ComponentProps } from "react";
import { Asset } from "@/components/ui/Asset";
import { Container } from "@/components/ui/Container";
import { Link } from "@/components/ui/Link";
import { Typography } from "@/components/ui/Typography";
import type { AssetStoryblok } from "@/generated/sb/sb-types";
import { Link as NextLink } from "@/i18n/routing";
import { isDarkMode } from "@/lib/utils/isDarkMode";
import { Reveal } from "../Reveal";

export type FeaturedCollectionAssetSide = "left" | "right";

export interface FeaturedCollectionAssetProps extends ComponentProps<"section"> {
	/** Where the image sits on desktop. Mobile always stacks text-then-image. */
	side?: FeaturedCollectionAssetSide;
	caption?: string;
	heading?: string;
	description?: string;
	linkLabel?: string;
	linkHref?: string;
	asset?: AssetStoryblok;
	bg?: string;
}

/**
 * FeaturedCollectionAsset — editorial image paired with heading, description,
 * and a CTA. Image side (left/right) is controlled via `side`.
 */
export const FeaturedCollectionAsset = ({
	side = "right",
	caption,
	heading,
	description,
	linkLabel,
	linkHref,
	asset,
	className,
	bg,
	...rest
}: FeaturedCollectionAssetProps) => {
	const hasLink = Boolean(linkLabel && linkHref);

	const assetBlock = asset ? (
		<Reveal asChild animation="fade-bottom" delay={100}>
			<div
				className={clsx(
					"relative w-full md:min-w-px md:flex-1 bg-background-bg-image overflow-hidden aspect-3/4",
				)}
			>
				<Asset asset={asset} sizes="(max-width: 768px) 100vw, 50vw" />
			</div>
		</Reveal>
	) : null;

	const darkMode = isDarkMode(bg);

	const textBlock = (
		<div
			className={clsx(
				"flex w-full md:min-w-px md:flex-1 flex-col items-start justify-between md:self-stretch py-grid-margin-sm gap-grid-component-spacing-2xl",
				side === "right" && "md:pl-grid-margin-sm",
				side === "left" && "md:py-grid-margin-sm md:pl-grid-margin-sm md:pr-0",
				bg && "px-4",
			)}
		>
			{(caption || heading) && (
				<div
					className={clsx(
						"flex w-full flex-col gap-grid-component-spacing-md text-text-dark-text-1",
						darkMode && "text-text-light-text-1",
					)}
				>
					{caption && (
						<Typography variant="label-sm-reg" as="p" className="leading-[1.15]">
							{caption}
						</Typography>
					)}
					{heading && (
						<Typography variant="heading-display-md" as="h2">
							{heading}
						</Typography>
					)}
				</div>
			)}
			{(description || hasLink) && (
				<div className="flex w-full flex-col gap-grid-component-spacing-2xl">
					{description && (
						<Typography
							variant="body-sm-reg"
							as="p"
							className={clsx("text-text-dark-text-1", darkMode && "text-text-light-text-1")}
						>
							{description}
						</Typography>
					)}
					{hasLink && linkHref && (
						<Link
							as={NextLink}
							href={linkHref}
							variant="secondary"
							size="medium"
							rightIcon={ArrowRightAlt}
							className={clsx(
								"w-full justify-between uppercase",
								darkMode && "text-text-light-text-1",
							)}
						>
							{linkLabel}
						</Link>
					)}
				</div>
			)}
		</div>
	);

	return (
		<section {...rest}>
			<Container size={bg ? "extralarge" : "large"} className={clsx("py-grid-section-vertical-lg")}>
				<div style={{ backgroundColor: bg ? bg : undefined, display: bg ? "block" : "contents" }}>
					<div
						className={clsx(
							"flex w-full flex-col gap-grid-component-spacing-md md:flex-row md:items-start md:overflow-hidden",
							side === "left" && "md:gap-grid-grid-gutter-lg",
							className,
						)}
					>
						{/* Mobile: text first then image. Desktop: matches `side`. */}
						<div className="contents md:hidden">
							{textBlock}
							{assetBlock}
						</div>
						<div className="hidden md:contents">
							{side === "left" ? (
								<>
									{assetBlock}
									{textBlock}
								</>
							) : (
								<>
									{textBlock}
									{assetBlock}
								</>
							)}
						</div>
					</div>
				</div>
			</Container>
		</section>
	);
};
